import React from 'react';
import {
  MarketingLayout,
  Section,
  Hero,
  SplitContent,
  CallToAction,
} from '@byscott-io/corebyscott';

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      <Hero
        headline="Features Built for Homeschool Families"
        subheadline="Every feature in LifeSkillsPath was designed with at-home assessment in mind. Here's how we help you track your students' growth."
        background="gray"
        layout="centered"
      />

      <Section background="white">
        <SplitContent
          title="Pre-K Readiness Assessment"
          description="Determine if your child is ready for kindergarten with comprehensive, age-appropriate evaluations that you can administer at home."
          features={[
            { title: 'Motor skills', description: 'fine and gross motor development checks' },
            { title: 'Social readiness', description: 'peer interaction and emotional regulation' },
            { title: 'Early academics', description: 'letter recognition, counting, and pre-reading skills' },
          ]}
          mediaPosition="right"
          media={
            <div className="rounded-xl bg-gray-100 p-8 shadow-lg">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-green-200 flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700">Motor Skills - Ready</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-green-200 flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700">Social Readiness - Ready</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-yellow-200 flex items-center justify-center">
                    <span className="text-yellow-600 text-xs font-bold">!</span>
                  </div>
                  <span className="text-sm text-gray-700">Pre-Reading - Developing</span>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <Section background="gray">
        <SplitContent
          title="Grade-Level Academic Assessments"
          description="Comprehensive K-12 assessments aligned with standard scope and sequence. Understand exactly where your student stands in each subject area."
          features={[
            { title: 'Reading & Language Arts', description: 'comprehension, vocabulary, and writing' },
            { title: 'Mathematics', description: 'from basic operations through algebra' },
            { title: 'Science & Social Studies', description: 'age-appropriate content knowledge' },
          ]}
          mediaPosition="left"
          media={
            <div className="rounded-xl bg-white p-8 shadow-lg">
              <h4 className="font-semibold text-gray-900">Grade 3 Assessment Results</h4>
              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Reading</span>
                    <span className="text-green-600">Above Grade Level</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-green-500" style={{ width: '85%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Math</span>
                    <span className="text-blue-600">At Grade Level</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-blue-500" style={{ width: '70%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Writing</span>
                    <span className="text-blue-600">At Grade Level</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-blue-500" style={{ width: '65%' }} />
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <Section background="white">
        <SplitContent
          title="Learning Differences Screening"
          description="Early identification tools help you recognize when a student might benefit from additional evaluation or support services."
          features={[
            { title: 'Dyslexia indicators', description: 'phonological awareness and reading pattern checks' },
            { title: 'Attention concerns', description: 'focus and executive function observations' },
            { title: 'Processing differences', description: 'auditory and visual processing screens' },
          ]}
          mediaPosition="right"
          media={
            <div className="rounded-xl bg-indigo-50 p-6 shadow-lg">
              <h4 className="font-semibold text-gray-900">Screening Summary</h4>
              <p className="mt-2 text-sm text-gray-600">
                Based on assessment responses, we recommend further evaluation in:
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                  </svg>
                  <span className="text-sm text-gray-700">Phonological Processing</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                This is a screening tool only. Please consult a qualified professional for diagnosis.
              </p>
            </div>
          }
        />
      </Section>

      <Section background="gray">
        <SplitContent
          title="Life Skills & Readiness"
          description="Track practical life skills development with age-appropriate checklists and assessments that grow with your student."
          features={[
            { title: 'Household skills', description: 'cooking, cleaning, and home maintenance' },
            { title: 'Financial literacy', description: 'budgeting, saving, and money management' },
            { title: 'Independence skills', description: 'time management, planning, and self-care' },
          ]}
          mediaPosition="left"
          media={
            <div className="rounded-xl bg-white p-6 shadow-lg">
              <h4 className="font-semibold text-gray-900">Life Skills Progress (Age 10)</h4>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Make simple meals</span>
                  <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">Mastered</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Do laundry</span>
                  <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-700">Learning</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Budget allowance</span>
                  <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">Mastered</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Plan weekly schedule</span>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">Not Started</span>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <Section background="white">
        <SplitContent
          title="Year-Over-Year Progress"
          description="See how your students grow over time with comprehensive progress reports that track improvements across all assessment areas."
          features={[
            { title: 'Historical data', description: 'compare results across multiple years' },
            { title: 'Growth visualization', description: 'clear charts showing improvement trends' },
            { title: 'Portfolio ready', description: 'exportable reports for documentation' },
          ]}
          mediaPosition="right"
          media={
            <div className="rounded-xl bg-gray-100 p-6 shadow-lg">
              <h4 className="font-semibold text-gray-900">3-Year Progress</h4>
              <div className="mt-4 flex items-end space-x-4 h-32">
                <div className="flex flex-col items-center">
                  <div className="h-16 w-8 rounded-t bg-indigo-300" />
                  <span className="mt-2 text-xs text-gray-500">2023</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="h-24 w-8 rounded-t bg-indigo-400" />
                  <span className="mt-2 text-xs text-gray-500">2024</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="h-32 w-8 rounded-t bg-indigo-600" />
                  <span className="mt-2 text-xs text-gray-500">2025</span>
                </div>
              </div>
              <p className="mt-4 text-center text-sm text-green-600 font-medium">+28% overall growth</p>
            </div>
          }
        />
      </Section>

      <Section background="gray">
        <SplitContent
          title="Multi-Family & Co-op Support"
          description="Perfect for homeschool co-ops and small schools. Manage multiple families with appropriate visibility controls for teachers and administrators."
          features={[
            { title: 'Family accounts', description: 'multiple students per family' },
            { title: 'Co-op groups', description: 'teachers can view assigned students' },
            { title: 'Role-based access', description: 'control who can assess and edit' },
          ]}
          mediaPosition="left"
          media={
            <div className="rounded-xl bg-white p-6 shadow-lg">
              <h4 className="font-semibold text-gray-900">Sunshine Co-op</h4>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm">Johnson Family</span>
                  <span className="text-xs text-gray-500">3 students</span>
                </div>
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm">Smith Family</span>
                  <span className="text-xs text-gray-500">2 students</span>
                </div>
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm">Williams Family</span>
                  <span className="text-xs text-gray-500">4 students</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Davis Family</span>
                  <span className="text-xs text-gray-500">2 students</span>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <CallToAction
        headline="Ready to get started?"
        description="Try LifeSkillsPath free and see how it can help you track your students' academic and life skills growth."
        primaryCta={{ label: 'Start Free Trial', href: '/register' }}
        background="primary"
      />
    </MarketingLayout>
  );
}
